import { useEffect, useState } from 'react';
import { Card, Button, Form, Modal, Toast, ToastContainer, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPenToSquare, faCode, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';

interface Field {
  key?: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
}
interface ContactForm {
  id: string;
  public_token: string;
  name: string;
  fields: Field[];
  submit_text: string;
  success_message: string;
  is_active: boolean;
}

const FIELD_TYPES = [
  { type: 'name', label: 'Name' },
  { type: 'email', label: 'Email' },
  { type: 'phone', label: 'Phone' },
  { type: 'text', label: 'Short text' },
  { type: 'textarea', label: 'Message' },
  { type: 'date', label: 'Date' },
  { type: 'select', label: 'Dropdown' }
];

const blankForm = (): ContactForm => ({
  id: '',
  public_token: '',
  name: 'Contact us',
  fields: [
    { type: 'name', label: 'Name', required: true },
    { type: 'email', label: 'Email', required: true },
    { type: 'textarea', label: 'Message', required: true }
  ],
  submit_text: 'Send',
  success_message: "Thanks! We'll get back to you shortly.",
  is_active: true
});

const ContactFormsPage = () => {
  const token = useSelector((state: RootState) => state.auth.token);
  const auth = token ? { Authorization: `Bearer ${token}` } : undefined;

  const [forms, setForms] = useState<ContactForm[]>([]);
  const [editing, setEditing] = useState<ContactForm | null>(null);
  const [embedFor, setEmbedFor] = useState<ContactForm | null>(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const load = () =>
    fetch(`${API_BASE}/forms`, { headers: auth })
      .then(r => (r.ok ? r.json() : { forms: [] }))
      .then(d => setForms(d.forms || []))
      .catch(() => undefined);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const url = editing.id ? `${API_BASE}/forms/${editing.id}` : `${API_BASE}/forms`;
      const res = await fetch(url, {
        method: editing.id ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', ...(auth || {}) },
        body: JSON.stringify(editing)
      });
      if (res.ok) {
        await load();
        setEditing(null);
        setToast({ msg: 'Form saved.', ok: true });
      } else setToast({ msg: 'Could not save.', ok: false });
    } catch {
      setToast({ msg: 'Could not save.', ok: false });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f: ContactForm) => {
    if (!window.confirm(`Delete "${f.name}"?`)) return;
    await fetch(`${API_BASE}/forms/${f.id}`, { method: 'DELETE', headers: auth });
    load();
  };

  // ---- field editing helpers (operate on `editing`) ----
  const patchField = (i: number, patch: Partial<Field>) =>
    setEditing(e =>
      e ? { ...e, fields: e.fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) } : e
    );
  const addField = (type: string) =>
    setEditing(e =>
      e
        ? {
            ...e,
            fields: [
              ...e.fields,
              {
                type,
                label: FIELD_TYPES.find(t => t.type === type)?.label || type,
                required: false,
                options: type === 'select' ? ['Option 1', 'Option 2'] : undefined
              }
            ]
          }
        : e
    );
  const removeField = (i: number) =>
    setEditing(e => (e ? { ...e, fields: e.fields.filter((_, idx) => idx !== i) } : e));
  const moveField = (i: number, dir: -1 | 1) =>
    setEditing(e => {
      if (!e) return e;
      const j = i + dir;
      if (j < 0 || j >= e.fields.length) return e;
      const fields = [...e.fields];
      [fields[i], fields[j]] = [fields[j], fields[i]];
      return { ...e, fields };
    });

  const embedSnippet = (f: ContactForm) =>
    `<!-- EasyChatDesk contact form -->\n<div data-ecd-form="${f.public_token}"></div>\n<script src="https://cdn.easychatdesk.com/forms.min.js" async></script>`;

  // ---- list view ----
  if (!editing) {
    return (
      <>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1 text-body-emphasis">Contact forms</h2>
            <p className="text-body-tertiary mb-0">
              Build a form, embed it on any page, and submissions arrive as tickets.
            </p>
          </div>
          <Button variant="primary" onClick={() => setEditing(blankForm())}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            New form
          </Button>
        </div>

        {forms.length === 0 ? (
          <Card>
            <Card.Body className="text-body-tertiary">No forms yet — create your first one.</Card.Body>
          </Card>
        ) : (
          <div className="d-flex flex-column gap-2">
            {forms.map(f => (
              <Card key={f.id}>
                <Card.Body className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-semibold">{f.name}</span>
                      {!f.is_active && <Badge bg="secondary">inactive</Badge>}
                    </div>
                    <div className="fs-9 text-body-tertiary">{f.fields.length} fields</div>
                  </div>
                  <div className="d-flex gap-1">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={() => {
                        setCopied(false);
                        setEmbedFor(f);
                      }}
                    >
                      <FontAwesomeIcon icon={faCode} className="me-1" /> Embed
                    </Button>
                    <Button variant="phoenix-secondary" size="sm" className="btn-icon" onClick={() => setEditing(f)} aria-label="Edit">
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </Button>
                    <Button variant="phoenix-danger" size="sm" className="btn-icon" onClick={() => remove(f)} aria-label="Delete">
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}

        <Modal show={!!embedFor} onHide={() => setEmbedFor(null)} centered>
          <Modal.Header closeButton>
            <Modal.Title className="fs-7">Embed this form</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="fs-9 text-body-tertiary">
              Paste this where you want the form to appear:
            </p>
            <pre className="bg-body-secondary p-3 rounded" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {embedFor ? embedSnippet(embedFor) : ''}
            </pre>
            <Button
              variant={copied ? 'success' : 'primary'}
              size="sm"
              onClick={() => {
                if (!embedFor) return;
                navigator.clipboard.writeText(embedSnippet(embedFor)).then(
                  () => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  },
                  () => setToast({ msg: 'Could not copy — copy it manually.', ok: false })
                );
              }}
            >
              <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="me-1" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </Modal.Body>
        </Modal>

        <ToastContainer position="bottom-end" className="p-3">
          <Toast show={!!toast} onClose={() => setToast(null)} delay={2500} autohide bg={toast?.ok ? 'success' : 'danger'}>
            <Toast.Body className="text-white">{toast?.msg}</Toast.Body>
          </Toast>
        </ToastContainer>
      </>
    );
  }

  // ---- editor view ----
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 text-body-emphasis">{editing.id ? 'Edit form' : 'New form'}</h2>
        <div className="d-flex gap-2">
          <Button variant="phoenix-secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save form'}
          </Button>
        </div>
      </div>

      <Card className="mb-3" style={{ maxWidth: 760 }}>
        <Card.Body className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label className="fs-9">Form name</Form.Label>
            <Form.Control value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
          </Form.Group>
          <Form.Check
            type="switch"
            label="Active (form accepts submissions)"
            checked={editing.is_active}
            onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3" style={{ maxWidth: 760 }}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Fields</h5>
          </div>

          <div className="d-flex flex-column gap-3">
            {editing.fields.map((f, i) => (
              <div key={i} className="border rounded p-3">
                <div className="d-flex gap-2 align-items-center mb-2">
                  <div className="d-flex flex-column">
                    <button className="btn btn-link p-0 lh-1 text-body-tertiary" onClick={() => moveField(i, -1)} aria-label="Up">▲</button>
                    <button className="btn btn-link p-0 lh-1 text-body-tertiary" onClick={() => moveField(i, 1)} aria-label="Down">▼</button>
                  </div>
                  <Form.Select
                    style={{ width: 150 }}
                    value={f.type}
                    onChange={e => patchField(i, { type: e.target.value, options: e.target.value === 'select' ? f.options || ['Option 1'] : undefined })}
                  >
                    {FIELD_TYPES.map(t => (
                      <option key={t.type} value={t.type}>{t.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Control
                    placeholder="Label"
                    value={f.label}
                    onChange={e => patchField(i, { label: e.target.value })}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Required"
                    className="flex-shrink-0"
                    checked={f.required}
                    onChange={e => patchField(i, { required: e.target.checked })}
                  />
                  <Button variant="phoenix-danger" size="sm" className="btn-icon flex-shrink-0" onClick={() => removeField(i)} aria-label="Remove">
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>
                {f.type === 'select' && (
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="One option per line"
                    value={(f.options || []).join('\n')}
                    onChange={e => patchField(i, { options: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mt-3 d-flex flex-wrap gap-2">
            {FIELD_TYPES.map(t => (
              <Button key={t.type} variant="phoenix-secondary" size="sm" onClick={() => addField(t.type)}>
                <FontAwesomeIcon icon={faPlus} className="me-1" /> {t.label}
              </Button>
            ))}
          </div>
        </Card.Body>
      </Card>

      <Card style={{ maxWidth: 760 }}>
        <Card.Body className="d-flex flex-column gap-3">
          <Form.Group>
            <Form.Label className="fs-9">Submit button text</Form.Label>
            <Form.Control value={editing.submit_text} onChange={e => setEditing({ ...editing, submit_text: e.target.value })} />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fs-9">Success message</Form.Label>
            <Form.Control as="textarea" rows={2} value={editing.success_message} onChange={e => setEditing({ ...editing, success_message: e.target.value })} />
          </Form.Group>
        </Card.Body>
      </Card>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast show={!!toast} onClose={() => setToast(null)} delay={2500} autohide bg={toast?.ok ? 'success' : 'danger'}>
          <Toast.Body className="text-white">{toast?.msg}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ContactFormsPage;
